// pages/tasks/ComplaintsListPage.jsx
//
// ONE common "Complaints" list page for every role -- no per-role
// duplicate pages, no client-side filtering of an org-wide list. The
// endpoint choice alone determines scope, and each endpoint is already
// safely scoped server-side:
//   - Developer / Data-Analyst -- ALWAYS self-scoped, ALWAYS IT_SUPPORT
//     only, via GET /my-complaints (created_by = req.user.id AND
//     task_type = IT_SUPPORT, resolved by code -- see
//     services/taskAssignmentService.js#listMyComplaints). This role
//     override wins regardless of the `scope` prop the route passes in.
//   - scope === "self-tickets" -- GET /my-tickets, self-scoped, EXCLUDING
//     IT_SUPPORT (created_by = req.user.id AND task_type <> IT_SUPPORT).
//   - Staff, on the shared "effective"-scope Complaints route -- ALSO
//     gets the polished rendering below (same visual design as Developer/
//     Data-Analyst's Complaints), but its DATA SOURCE is completely
//     unchanged: still GET /tasks/user/:id when tasks:view is not granted
//     (the existing self-scoped, IDOR-hardened fallback), exactly as
//     before this file added any Developer/Data-Analyst-specific UI.
//   - Every other role (Admin/Manager/Technician/Accounts), scope ===
//     "effective" -- unchanged existing behavior:
//       - can('tasks','view') === true  -> GET /tasks. taskController.
//         getTasks applies the caller's resolved data_scope (ALL/UNIT/
//         ASSIGNED/...) directly in the SQL WHERE clause (fail-closed if
//         unresolved) -- Admin sees ALL, Manager sees their own UNIT, with
//         zero React-side filtering.
//       - can('tasks','view') === false -> GET /tasks/user/:id, self-scoped
//         by taskController.listTasksByUser's requireSelfOrDeny (id must
//         equal req.user.id).
// Row detail navigates to the standalone Task Detail Page
// (src/pages/tasks/TaskDetailPage.jsx, at /<role>/tasks/:taskId -- already
// permission-gated internally for Add Remark/Change Status).
//
// The Chat action (TaskChatPanel) is authorized separately and server-side
// via GET/POST /tasks/:id/chat's own participant check -- NOT via
// tasks:add_remarks or the `can()` checks above, so Developer/Data-Analyst
// (no add_remarks grant) can still chat on their own tickets.
//
// TAT column: formats the EXISTING `due_at` (already computed server-side
// by the same business-hours TAT engine the Create Ticket/Raise Complaint
// form's own "Estimated TAT" preview uses) via tatUtils.js -- no new TAT
// calculation. Latest Chat column: uses the additive unread_count/
// last_message/last_message_at fields now returned by /my-complaints,
// /my-tickets (services/taskAssignmentService.js#listMyCreatedTasks) and
// already returned by /tasks/user/:id (taskController.js#listTasksByUser)
// -- same underlying task_remarks data Chat itself reads, just previewed
// inline; never a second endpoint.
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Badge, Spinner, Button, Form } from "react-bootstrap";
import { FaTicketAlt, FaClock, FaSpinner, FaCheckCircle, FaPlus, FaEye, FaSyncAlt, FaCommentDots, FaInbox, FaExclamationTriangle, FaFileExcel, FaCheck, FaPen } from "react-icons/fa";
import * as XLSX from "xlsx";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import { usePermissions } from "../../context/PermissionContext";
import TaskChatPanel from "./TaskChatPanel";
import TaskCreateModal from "./TaskCreateModal";
import OwnTicketModal from "./OwnTicketModal";
import CompleteTicketModal from "./CompleteTicketModal";
import { truncate, getTatInfo } from "./tatUtils";
import { statusPillClass, priorityPillClass, StatusTatCell } from "./StatusChips";
import InlineStatusPicker from "./InlineStatusPicker";
import OwnTicketStatusPicker from "./OwnTicketStatusPicker";
import EditOwnTicketModal from "./EditOwnTicketModal";
import { summarizeNames } from "./dataAnalystTat";
import { taskDisplayTitle } from "../../utils/taskDisplay";
import "./PortalHomeCards.css";
// Page-scoped visual reskin only -- see file header at the bottom of this
// stylesheet. Never edit PortalHomeCards.css's own base ".tlp-*" rules
// here, since that file is also shared by DeveloperDashboard.jsx/
// DataAnalystDashboard.jsx, and never edit AdminUnitPage.css's base
// ".unit-*" rules either, since that family is shared by every Admin
// "unit" page -- everything below is scoped under the unique
// "complaints-list-theme" wrapper class added to this page's own root.
import "./ComplaintsListPage.overrides.css";

function normalizeRows(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

// The reason a list load failed, shown under the generic error so the page
// itself reports what the API said (a permission denial, a server error, a
// timeout) instead of one message for every cause.
function describeLoadError(err) {
  const status = err?.response?.status;
  const body = err?.response?.data || {};
  if (status === 403) return "You do not have permission to view these records (403).";
  if (status) {
    const code = body.code ? ` ${body.code}` : "";
    const message = body.message || body.error;
    return `Server responded ${status}${code}${message ? `: ${message}` : ""}`;
  }
  if (err?.code === "ECONNABORTED") return "The request timed out.";
  return "The server could not be reached.";
}

const DEV_OR_DA_ROLES = new Set(["developer", "data-analyst"]);
const OPEN_ISH_ORDER = { pending: 0, in_progress: 1, completed: 2, closed: 2 };
const PAGE_SIZE = 10;
// Statuses an Own Ticket can never be "Complete"d from directly -- an
// already-completed ticket gets the Edit action instead (see the Actions
// cell), and closed/cancelled tickets are excluded from either action.
const NON_COMPLETABLE_STATUSES = new Set(["completed", "closed", "cancelled"]);

const titleCase = (v) => String(v || "-").replace(/_/g, " ").replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());
const formatDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return v; }
};
const formatDateTime = (v) => {
  if (!v) return "";
  try { return new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return v; }
};

// Purely additive visual flag -- no new data, no backend call. A row is
// "Escalated" when it's still open and has already breached its existing
// due_at (same TAT breach getTatInfo/tatUtils.js already computes for the
// polished Status/TAT cell elsewhere on this page); completed/closed rows
// are excluded since a past breach there is history, not something needing
// attention right now.
function isEscalated(row) {
  const status = String(row.status || "").toLowerCase();
  if (["completed", "closed"].includes(status)) return false;
  return getTatInfo(row).tone === "red";
}

function summarize(rows) {
  const counts = { total: rows.length, pending: 0, in_progress: 0, completed: 0, unread: 0, overdue: 0 };
  rows.forEach((row) => {
    const status = String(row.status || "").toLowerCase();
    if (status === "pending") counts.pending += 1;
    else if (status === "in_progress") counts.in_progress += 1;
    else if (status === "completed" || status === "closed") counts.completed += 1;
    if (Number(row.unread_count) > 0) counts.unread += 1;
    // Same TAT formula the Status/TAT cell already renders (getTatInfo) --
    // "red" tone is either an open task past due_at, or one completed after
    // it. No second TAT calculation.
    if (getTatInfo(row).tone === "red") counts.overdue += 1;
  });
  return counts;
}

// "YYYY-MM" bucket key from a row's created_at, for the Month filter (§16)
// -- and its short display label ("Aug 2026"). Client-side only, over the
// same already-fetched rows every other filter here already narrows.
function monthKey(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function monthLabel(key) {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
}

// One row per exported record, built from exactly the fields this scope's
// own table already renders -- never a field the caller can't already see
// on screen. Same titleCase/formatDate helpers the table cells use, so the
// spreadsheet reads identically to what's on screen.
function buildExportRows(rows, { isComplaintsTable, isSelfTickets, showSchoolColumn }) {
  return rows.map((row, idx) => {
    // Complaints table: a plain 1..N serial number (matches the on-screen
    // "#" column) rather than the internal ticket id, which isn't
    // meaningful to a staff member raising a complaint.
    const record = { "#": isComplaintsTable ? idx + 1 : row.id, [isSelfTickets ? "Task" : "Title"]: taskDisplayTitle(row) };
    if (isSelfTickets) record["Task Type"] = row.task_type_name || "—";
    if (isComplaintsTable) record["Assignee"] = row.assigned_to_name || "Unassigned";
    if (isComplaintsTable) record["Category"] = row.category_name || "—";
    // Phase 66: every selected subcategory / school (multi-select Data
    // Analyst Own Ticket), else the single value as before.
    const subNames = (row.sub_categories || []).map((s) => s.name);
    if (isComplaintsTable && (subNames.length || row.sub_category_name)) record["Sub Category"] = subNames.length ? subNames.join(", ") : row.sub_category_name;
    if (showSchoolColumn) record["Schools"] = (row.schools || []).map((s) => s.name).join(", ") || row.school_name || "—";
    if (isSelfTickets) {
      record["Project"] = row.project_name || "—";
      record["Module"] = row.project_module_name || "—";
    }
    if (isSelfTickets) record["Assigned To"] = row.assigned_to_name || "Unassigned";
    record["Status"] = titleCase(row.status);
    if (!isComplaintsTable) record["Priority"] = row.priority ? titleCase(row.priority) : "—";
    record["Created"] = formatDate(row.created_at);
    if (isComplaintsTable) record["Completed"] = formatDate(row.completed_at);
    if (isSelfTickets) record["Due"] = formatDate(row.due_at);
    return record;
  });
}

function excelDownload(rows, filename, sheetName = "Sheet1") {
  const wb = XLSX.utils.book_new();
  const sheet = XLSX.utils.json_to_sheet(rows);
  XLSX.utils.book_append_sheet(wb, sheet, sheetName.slice(0, 31));
  XLSX.writeFile(wb, filename);
}

// Shared small "primary action" button -- same component/markup used by
// the header and the empty state, so the two never visually drift apart.
function PrimaryActionButton({ label, onClick }) {
  return (
    <Button className="tlp-btn tlp-btn-primary" onClick={onClick}>
      <FaPlus style={{ fontSize: 11 }} /> {label}
    </Button>
  );
}

// Jira-style assignee cell -- round initials avatar (a stable pastel color
// derived from the name, so the same person always gets the same color)
// + name, instead of plain text. "Unassigned" keeps a neutral dashed-look
// avatar with no initials.
const AVATAR_PALETTE = ["#2563eb", "#7c3aed", "#0f766e", "#b45309", "#be185d", "#15803d", "#0891b2", "#c2410c"];
function initials(name) {
  const s = String(name || "").trim();
  if (!s) return "?";
  return s.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}
function avatarColor(name) {
  const s = String(name || "");
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}
function AssigneeCell({ name }) {
  if (!name) {
    return (
      <span className="tlp-assignee tlp-assignee-empty">
        <span className="tlp-avatar tlp-avatar-empty" aria-hidden="true">—</span>
        Unassigned
      </span>
    );
  }
  return (
    <span className="tlp-assignee">
      <span className="tlp-avatar" style={{ background: avatarColor(name) }} aria-hidden="true">{initials(name)}</span>
      {name}
    </span>
  );
}

function LatestChatCell({ row, onOpen }) {
  if (!row.last_message) return <span className="small text-muted">—</span>;
  return (
    <div className="tlp-chat-preview" title={row.last_message} onClick={onOpen} role="button">
      <div className="tlp-chat-top">
        <span className="tlp-chat-time">{formatDateTime(row.last_message_at)}</span>
        {Number(row.unread_count) > 0 && <span className="tlp-unread-badge">{row.unread_count}</span>}
      </div>
      <span className="tlp-chat-text">{truncate(row.last_message, 42)}</span>
    </div>
  );
}

export default function ComplaintsListPage({ scope = "effective", title = "Complaints", description = "Complaints scoped to your effective access -- nothing outside it is fetched." }) {
  const { user } = useContext(AuthContext) || {};
  const navigate = useNavigate();
  const { can, loading: permsLoading } = usePermissions();
  const isDevOrDA = DEV_OR_DA_ROLES.has(user?.role);
  const isStaff = user?.role === "staff";
  // Role override -- see file header. Wins over whatever `scope` the route
  // wiring passed in, so Developer/Data-Analyst's "Complaints" is always
  // their own raised IT_SUPPORT rows, never their ASSIGNED-scope grant.
  const effectiveScope = isDevOrDA && scope === "effective" ? "self-complaints" : scope;
  const isSelfComplaints = effectiveScope === "self-complaints";
  const isSelfTickets = effectiveScope === "self-tickets";
  // Staff's OWN Complaints route stays on "effective" scope (its data
  // source -- GET /tasks/user/:id via the existing tasks:view fallback --
  // is completely unchanged); it just ALSO qualifies for the same
  // polished rendering/complaint-table design as Developer/Data-Analyst's
  // Complaints, so all three roles look consistent.
  const isStaffComplaints = isStaff && effectiveScope === "effective";
  const isComplaintsTable = isSelfComplaints || isStaffComplaints;
  // Developer/Data-Analyst parity: "Raised Complaints" (routed to a
  // Technician) vs "Own Tickets" (self-owned, via OwnTicketModal) tabs.
  // Explicit page-intent flag, derived from the SAME DEV_OR_DA_ROLES/
  // isDevOrDA cohort this file already uses for every other Developer/
  // Data-Analyst behavior on this page (self-scoped endpoint choice, the
  // Own Ticket action, etc.) -- never a single hardcoded role string, so
  // Developer and Data Analyst can never drift apart on this again. Staff/
  // Technician/Admin/Manager/Accounts never reach isSelfComplaints at all
  // (see effectiveScope above), so this stays false for them regardless.
  const showOwnershipTabs = isSelfComplaints && isDevOrDA;
  // Phase 60: School (set from the Data Analyst's Own Ticket modal) --
  // Data Analyst's own Complaints only; tickets without one show "—".
  const showSchoolColumn = isSelfComplaints && user?.role === "data-analyst";
  // Phase 66: Data Analyst can edit her own OPEN Own Tickets (title,
  // schools, category, subcategories, TAT) -- the backend re-checks role,
  // ownership and status (PUT /tasks/:id/own-ticket).
  const canEditOwnTicket = showSchoolColumn && showOwnershipTabs;
  // The richer, professional rendering (light header card/KPI strip/
  // toolbar) is scoped to exactly these self-scoped Complaints/My Tickets
  // pages -- every other role's Complaints page is untouched.
  const isPolishedPage = isSelfComplaints || isSelfTickets || isStaffComplaints;
  const effectiveTitle = isComplaintsTable ? "My Complaints" : isSelfTickets ? "My Tickets" : title;
  const eyebrow = isComplaintsTable ? "IT Support" : "Work Tracking";
  const effectiveDescription = isComplaintsTable
    ? "Track your IT support requests and resolution progress."
    : isSelfTickets
      ? "Track work tickets you have created."
      : description;

  // Primary action(s) -- open the SAME complaint-mode form as the
  // standalone /<role>/tasks/raise-complaint route, in a modal on THIS page,
  // instead of navigating away. That standalone route is untouched and
  // still works directly (deep links, fallback).
  //
  // Developer/Data-Analyst's own Complaints page gets a SECOND action,
  // "Own Ticket" -- a self-owned work item that is NOT routed to a
  // technician, as opposed to "Raise Complaint" (technician-routed,
  // mode="complaint"). Own Ticket is intentionally a DIFFERENT component
  // (OwnTicketModal, modeled on the Technician's own "Add Task" -- Staff/
  // User, Category, Subcategory, Task Date, TAT, no Project/Module/Phase/
  // Milestone) rather than TaskCreateForm's generic ticket mode, which
  // showed the wrong (work-ticket) fields for this flow.
  //
  // Raise Complaint never asks for a technician: the backend assigns one
  // from the caller's own unit (see TaskCreateForm.jsx's doc comment).
  const primaryActions = isSelfComplaints
    ? [
        { key: "complaint", label: "Raise Complaint", kind: "task-create-modal", mode: "complaint" },
        { key: "own-ticket", label: "Own Ticket", kind: "own-ticket" },
      ]
    : isStaffComplaints
      ? [{ key: "complaint", label: "Raise Complaint", kind: "task-create-modal", mode: "complaint" }]
      : isSelfTickets
        ? [{ key: "ticket", label: "Create Ticket", kind: "task-create-modal", mode: undefined }]
        : [];
  const showPrimaryActions = primaryActions.length > 0 && can("tasks", "create");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chatTaskId, setChatTaskId] = useState(null);
  // Holds the primaryActions entry the shared TaskCreateModal (Raise
  // Complaint / Create Ticket -- both "kind: task-create-modal") is
  // currently open for (null when closed). "Own Ticket" ("kind:
  // own-ticket") is a SEPARATE modal (OwnTicketModal, its own boolean
  // below) -- a different component entirely, never routed through this
  // state.
  const [activeCreateAction, setActiveCreateAction] = useState(null);
  const [showOwnTicketModal, setShowOwnTicketModal] = useState(false);
  // Own Tickets "Mark Completed" / "Edit completion details" -- holds the
  // row being acted on, or null when the modal is closed.
  const [completeTarget, setCompleteTarget] = useState(null);
  // Phase 66: Data Analyst "Edit" on an open Own Ticket -- the task id being
  // edited (EditOwnTicketModal), or null.
  const [editOwnTicketId, setEditOwnTicketId] = useState(null);
  // The list row of a COMPLETED own ticket opened from Edit: its details are
  // locked, its completion details are corrected in the same Edit modal.
  const [editOwnTicketCompletion, setEditOwnTicketCompletion] = useState(null);
  // Data Analyst IT Complaints, Part B: only meaningful when
  // showOwnershipTabs is true. "raised" is the recommended default tab.
  const [activeTab, setActiveTab] = useState("raised");
  const [search, setSearch] = useState("");
  // Optional deep link (e.g. the Manager dashboard's KPI cards open
  // /manager/complaints?status=pending): pre-selects the Status filter.
  // Only these known values are honoured; anything else starts unfiltered.
  const [statusFilter, setStatusFilter] = useState(() => {
    try {
      const s = new URLSearchParams(window.location.search).get("status");
      return ["pending", "in_progress", "completed"].includes(s) ? s : "";
    } catch { return ""; }
  });
  const [priorityFilter, setPriorityFilter] = useState("");
  // My Tickets only (§15/§16) -- Task Type / Project / Month, same
  // client-side-over-already-fetched-rows pattern as status/priority above.
  const [taskTypeFilter, setTaskTypeFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [assignedToFilter, setAssignedToFilter] = useState("");
  const [page, setPage] = useState(1);

  // `tabOverride` lets a just-created-record handler (see handleOwnTicket
  // Created/handleComplaintCreated below) force-fetch the correct tab's
  // data immediately, even though the `setActiveTab` call that also fires
  // alongside it hasn't re-rendered yet -- without this, a create on an
  // already-active tab (no state change, so the effect below wouldn't
  // re-fire) would leave the new record invisible until a manual refresh.
  const fetchRows = (tabOverride) => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    const ownershipView = showOwnershipTabs ? (tabOverride || activeTab) : undefined;
    // "self-complaints" / "self-tickets" always use the dedicated,
    // self-scoped, task-type-discriminated endpoints -- regardless of the
    // caller's tasks:view grant -- because a newly-created, unassigned
    // ticket must still show up even when it wouldn't yet satisfy an
    // ASSIGNED-scoped tasks:view. Staff's own endpoint choice is entirely
    // unchanged (still the existing can('tasks','view') fallback).
    // Manager is special-cased to always use /tasks (unit-scoped
    // server-side, zero React-side filtering) regardless of the tasks:view
    // grant -- ManagerDashboard.js's own task table already calls /tasks
    // for this same role with no permission check at all, so gating THIS
    // page's identical /tasks call behind tasks:view just meant a Manager
    // missing that specific grant saw two different datasets depending on
    // which page they were on (Dashboard: full unit; Complaints: silently
    // fell back to /tasks/user/:id, self-scoped to their own tickets).
    // Every other role (Admin/Technician/Accounts/Staff) keeps the
    // existing tasks:view-gated fallback unchanged.
    const endpoint = isSelfComplaints
      ? "/my-complaints"
      : isSelfTickets
        ? "/my-tickets"
        : (user?.role === "manager" || can("tasks", "view")) ? "/tasks" : `/tasks/user/${user.id}`;
    // pageSize is an EXISTING, already-supported query param on
    // /my-complaints /my-tickets (buildPagination clamps to <= 200) --
    // raised here only for the self-scoped personal lists so the summary
    // cards below reflect more than just the default first-20-rows page.
    // complaintsOnly is the same additive, opt-in filter /my-complaints
    // already applies for Developer/DA -- for Staff's fallback endpoint it
    // ensures Complaints never shows a non-IT_SUPPORT task.
    const params = (isSelfComplaints || isSelfTickets)
      ? { pageSize: 200, ...(ownershipView ? { view: ownershipView } : {}) }
      : (isStaffComplaints && endpoint === `/tasks/user/${user.id}`)
        ? { complaintsOnly: 1 }
        : undefined;
    api
      .get(endpoint, params ? { params } : undefined)
      .then((res) => setRows(normalizeRows(res.data)))
      .catch((err) => {
        setRows([]);
        setError({ message: `Failed to load ${effectiveTitle.toLowerCase()}.`, detail: describeLoadError(err) });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (permsLoading) return;
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permsLoading, user?.id, effectiveScope, activeTab]);

  const summary = useMemo(() => summarize(rows), [rows]);

  // Filter options are derived from the data actually present -- never a
  // fixed/invented list.
  const statusOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.status).filter(Boolean))).sort(
      (a, b) => (OPEN_ISH_ORDER[String(a).toLowerCase()] ?? 9) - (OPEN_ISH_ORDER[String(b).toLowerCase()] ?? 9)
    ),
    [rows]
  );
  const priorityOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.priority).filter(Boolean))),
    [rows]
  );
  const taskTypeOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.task_type_name).filter(Boolean))).sort(),
    [rows]
  );
  const projectOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.project_name).filter(Boolean))).sort(),
    [rows]
  );
  const monthOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => monthKey(r.created_at)).filter(Boolean))).sort().reverse(),
    [rows]
  );
  const assignedToOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.assigned_to_name || "Unassigned"))).sort(),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter && String(row.status || "") !== statusFilter) return false;
      if (priorityFilter && String(row.priority || "") !== priorityFilter) return false;
      if (taskTypeFilter && String(row.task_type_name || "") !== taskTypeFilter) return false;
      if (projectFilter && String(row.project_name || "") !== projectFilter) return false;
      if (monthFilter && monthKey(row.created_at) !== monthFilter) return false;
      if (assignedToFilter && String(row.assigned_to_name || "Unassigned") !== assignedToFilter) return false;
      if (q && !taskDisplayTitle(row).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, statusFilter, priorityFilter, taskTypeFilter, projectFilter, monthFilter, assignedToFilter]);

  // Reset to page 1 whenever the filtered set changes shape, so a filter
  // never leaves the view stuck on an out-of-range page. Switching tabs
  // resets it too (§ Search/Status/Pagination -- "changing tab should
  // preferably reset page number to 1").
  useEffect(() => { setPage(1); }, [search, statusFilter, priorityFilter, taskTypeFilter, projectFilter, monthFilter, assignedToFilter, rows.length, activeTab]);

  // Data Analyst IT Complaints, Part B: after a successful create, close
  // the modal (each modal's own onClose already handles that), refresh
  // data, and land on the tab where the new record actually belongs --
  // fetchRows(tabOverride) guarantees the correct ?view= is used
  // immediately even if activeTab was already on that tab (see fetchRows's
  // own comment for why the override is necessary).
  const handleOwnTicketCreated = () => {
    setActiveTab("own");
    fetchRows("own");
  };
  const handleComplaintCreated = () => {
    if (showOwnershipTabs) {
      setActiveTab("raised");
      fetchRows("raised");
    } else {
      fetchRows();
    }
  };

  const handleExportExcel = () => {
    // filteredRows is already tab-scoped (rows itself came from the tab's
    // own ?view=raised|own fetch) -- exporting it as-is never merges the
    // two tabs; the filename/sheet name just reflects that.
    const exportRows = buildExportRows(filteredRows, { isComplaintsTable, isSelfTickets, showSchoolColumn });
    const stamp = new Date().toISOString().slice(0, 10);
    const exportLabel = showOwnershipTabs
      ? (activeTab === "own" ? "Own_Tickets" : "Raised_Complaints")
      : effectiveTitle.replace(/\s+/g, "_");
    excelDownload(exportRows, `${exportLabel}_${stamp}.xlsx`, exportLabel.replace(/_/g, " "));
  };

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredRows, currentPage]
  );

  const openCreateModal = (action) => {
    if (action.kind === "own-ticket") {
      setShowOwnTicketModal(true);
      return;
    }
    setActiveCreateAction(action);
  };
  const closeCreateModal = () => setActiveCreateAction(null);

  // Actual rendered column count (matches the <thead> above exactly) --
  // used as the empty-state row's colSpan so it never drifts out of sync
  // with the real header as columns are conditionally added per scope.
  const columnCount =
    2 /* #, Task/Title */
    + (isComplaintsTable ? 3 : 0) /* Assignee, Category, Project/Module */
    + (showSchoolColumn ? 1 : 0) /* School */
    + (isSelfTickets ? 2 : 0) /* Project/Module, Assigned To */
    + 1 /* Status(/TAT) */
    + (!isComplaintsTable ? 1 : 0) /* Priority */
    + (isComplaintsTable ? 1 : 0) /* Latest Chat */
    + 1 /* Created */
    + (isComplaintsTable ? 1 : 0) /* Completed */
    + (isSelfTickets ? 1 : 0) /* Due */
    + 1; /* Actions */

  return (
    <div className={`unit-page complaints-list-theme${isPolishedPage ? " tlp-page" : ""}`}>
      {isPolishedPage ? (
        <>
          <div className="tlp-header">
            <div>
              <div className="tlp-eyebrow">{eyebrow}</div>
              <h1 className="tlp-title">{effectiveTitle}</h1>
              <p className="tlp-subtitle">{effectiveDescription}</p>
            </div>
            <div className="tlp-hactions">
              {showPrimaryActions && primaryActions.map((action) => (
                <PrimaryActionButton key={action.key} label={action.label} onClick={() => openCreateModal(action)} />
              ))}
              {rows.length > 0 && (
                <Button className="tlp-btn tlp-btn-ghost" onClick={handleExportExcel} title="Export to Excel">
                  <FaFileExcel style={{ fontSize: 12 }} /> Export
                </Button>
              )}
              <Button className="tlp-btn tlp-btn-ghost" onClick={() => fetchRows()} disabled={loading} title="Refresh">
                <FaSyncAlt style={{ fontSize: 12 }} />
              </Button>
            </div>
          </div>

          {/* Data Analyst IT Complaints, Part B: two tabs -- switching one
              re-fetches from the server-side-filtered ?view=raised|own
              endpoint (never a client-side split of one fetched list). The
              table/cards/search/filter/export/pagination below are the
              SAME existing markup either way; only the data source and a
              couple of labels change. */}
          {showOwnershipTabs && (
            <div className="tlp-tabs" role="tablist" aria-label="Complaints ownership">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "raised"}
                className={`tlp-tab${activeTab === "raised" ? " active" : ""}`}
                onClick={() => setActiveTab("raised")}
              >
                Raised Complaints
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "own"}
                className={`tlp-tab${activeTab === "own" ? " active" : ""}`}
                onClick={() => setActiveTab("own")}
              >
                Own Tickets
              </button>
            </div>
          )}

          <div className="tlp-kpi-strip">
            <div className="tlp-kpi">
              <div className="tlp-kpi-icon tlp-kpi-icon-blue"><FaTicketAlt /></div>
              <div><span className="tlp-kpi-label">Total {showOwnershipTabs ? (activeTab === "own" ? "Own Tickets" : "Complaints") : (isComplaintsTable ? "Complaints" : "Tickets")}</span><strong className="tlp-kpi-val">{loading ? "…" : summary.total}</strong></div>
            </div>
            <div className="tlp-kpi">
              <div className="tlp-kpi-icon tlp-kpi-icon-amber"><FaClock /></div>
              <div><span className="tlp-kpi-label">Pending</span><strong className="tlp-kpi-val">{loading ? "…" : summary.pending}</strong></div>
            </div>
            <div className="tlp-kpi">
              <div className="tlp-kpi-icon tlp-kpi-icon-info"><FaSpinner /></div>
              <div><span className="tlp-kpi-label">In Progress</span><strong className="tlp-kpi-val">{loading ? "…" : summary.in_progress}</strong></div>
            </div>
            <div className="tlp-kpi">
              <div className="tlp-kpi-icon tlp-kpi-icon-green"><FaCheckCircle /></div>
              <div><span className="tlp-kpi-label">Completed</span><strong className="tlp-kpi-val">{loading ? "…" : summary.completed}</strong></div>
            </div>
            {isComplaintsTable && (
              <div className="tlp-kpi">
                <div className="tlp-kpi-icon tlp-kpi-icon-purple"><FaCommentDots /></div>
                <div><span className="tlp-kpi-label">Unread Chats</span><strong className="tlp-kpi-val">{loading ? "…" : summary.unread}</strong></div>
              </div>
            )}
            {isSelfTickets && (
              <div className="tlp-kpi">
                <div className="tlp-kpi-icon tlp-kpi-icon-red"><FaExclamationTriangle /></div>
                <div><span className="tlp-kpi-label">TAT Breached</span><strong className="tlp-kpi-val">{loading ? "…" : summary.overdue}</strong></div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="unit-header">
          <div>
            <div className="unit-page-label">{effectiveTitle}</div>
            <h1>{effectiveTitle}</h1>
            <p>{effectiveDescription}</p>
          </div>
          <div className="unit-header-actions">
            <Button variant="light" className="unit-btn unit-btn-light" onClick={() => fetchRows()} disabled={loading} title="Refresh">
              <FaSyncAlt style={{ fontSize: 12 }} /> Refresh
            </Button>
          </div>
        </div>
      )}

      <div className={isPolishedPage ? "tlp-card" : "unit-card"}>
        {rows.length > 0 && (
          <div className="tlp-card-head">
            <strong>{effectiveTitle}<span className="tlp-count"> ({filteredRows.length}{filteredRows.length !== rows.length ? ` of ${rows.length}` : ""})</span></strong>
            {rows.length > 0 && (
              <div className="tlp-toolbar">
                <Form.Control
                  className="tlp-search"
                  placeholder={`Search ${isComplaintsTable ? "complaints" : isSelfTickets ? "tickets" : effectiveTitle.toLowerCase()}...`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Form.Select className="tlp-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All Status</option>
                  {statusOptions.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
                </Form.Select>
                {/* Priority filter is only meaningful for My Tickets -- the
                    Complaints table keeps a single clean search+status row
                    per design, since priority filtering wasn't requested
                    there. */}
                {!isComplaintsTable && (
                  <Form.Select className="tlp-select" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                    <option value="">Priority: All</option>
                    {priorityOptions.map((p) => <option key={p} value={p}>{titleCase(p)}</option>)}
                  </Form.Select>
                )}
                {/* Task Type / Project / Month -- My Tickets only (§15/§16).
                    Options are derived from the data actually present, same
                    as status/priority above -- never a fixed/invented list. */}
                {isSelfTickets && (
                  <>
                    <Form.Select className="tlp-select" value={taskTypeFilter} onChange={(e) => setTaskTypeFilter(e.target.value)}>
                      <option value="">Task Type: All</option>
                      {taskTypeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                    </Form.Select>
                    <Form.Select className="tlp-select" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
                      <option value="">Project: All</option>
                      {projectOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                    </Form.Select>
                    <Form.Select className="tlp-select" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
                      <option value="">All Months</option>
                      {monthOptions.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                    </Form.Select>
                    <Form.Select className="tlp-select" value={assignedToFilter} onChange={(e) => setAssignedToFilter(e.target.value)}>
                      <option value="">Assigned To: All</option>
                      {assignedToOptions.map((a) => <option key={a} value={a}>{a}</option>)}
                    </Form.Select>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div className="unit-table-wrapper">
          {permsLoading || loading ? (
            <div className={isPolishedPage ? "tlp-empty" : "unit-loader"}>
              <Spinner animation="border" />
              <p>Loading {effectiveTitle.toLowerCase()}...</p>
            </div>
          ) : error ? (
            <div className={isPolishedPage ? "tlp-empty" : "unit-empty"}>
              <div className={isPolishedPage ? "tlp-empty-icon" : "unit-empty-icon"}><FaExclamationTriangle /></div>
              <p>{error.message}</p>
              {error.detail && <small className="text-muted d-block mb-2">{error.detail}</small>}
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => fetchRows()}>
                Retry
              </button>
            </div>
          ) : rows.length === 0 && isPolishedPage ? (
            <div className="tlp-empty">
              <div className="tlp-empty-icon"><FaTicketAlt /></div>
              <h5>
                {showOwnershipTabs && activeTab === "own"
                  ? "No own tickets yet"
                  : isComplaintsTable ? "No complaints raised yet" : "No work tickets yet"}
              </h5>
              <p>
                {showOwnershipTabs && activeTab === "own"
                  ? "Own Ticket is self-owned work you create and assign to yourself -- create one whenever you need to track it."
                  : isComplaintsTable
                    ? "Raise an IT support complaint whenever you need assistance."
                    : "Create a ticket to record and track your development, analysis, testing or other work."}
              </p>
              {showPrimaryActions && (
                <div className="d-flex gap-2">
                  {primaryActions.map((action) => (
                    <PrimaryActionButton key={action.key} label={action.label} onClick={() => openCreateModal(action)} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <Table responsive className={isPolishedPage ? "unit-table tlp-table" : "unit-table"}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{isSelfTickets ? "Task" : "Title"}</th>
                    {isComplaintsTable && <th>Assignee</th>}
                    {isComplaintsTable && <th>Category</th>}
                    {showSchoolColumn && <th>Schools</th>}
                    {isSelfTickets && <th>Project / Module</th>}
                    {isSelfTickets && <th>Assigned To</th>}
                    <th className="text-center">{(isComplaintsTable || isSelfTickets) ? "Status / TAT" : "Status"}</th>
                    {!isComplaintsTable && <th className="text-center">Priority</th>}
                    {isComplaintsTable && <th>Latest Chat</th>}
                    <th>Created</th>
                    {isComplaintsTable && <th>Completed</th>}
                    {isSelfTickets && <th>Due</th>}
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length > 0 ? (
                    pageRows.map((row, idx) => (
                      <tr key={row.id} className={!isComplaintsTable && !isSelfTickets && isEscalated(row) ? "tlp-row-escalated" : undefined}>
                        <td className="small">
                          {isComplaintsTable
                            ? (currentPage - 1) * PAGE_SIZE + idx + 1
                            : `#${row.id}`}
                        </td>
                        <td>
                          <span className="tlp-row-title">{taskDisplayTitle(row)}</span>
                          {isComplaintsTable && row.description && (
                            <span className="tlp-row-desc" title={row.description}>{truncate(row.description, 60)}</span>
                          )}
                          {/* Task Type as the Task column's secondary line
                              (§3/§2) -- actual task_types data (see
                              taskAssignmentService.js#listMyCreatedTasks'
                              task_type_name join), never inferred from the
                              title/category. */}
                          {isSelfTickets && <span className="tlp-row-desc">{row.task_type_name || "—"}</span>}
                        </td>
                        {isComplaintsTable && <td className="small"><AssigneeCell name={row.assigned_to_name} /></td>}
                        {isComplaintsTable && (
                          <td className="small">
                            {row.category_name || "—"}
                            {row.sub_categories?.length > 1 ? (
                              <span className="tlp-subcat" title={row.sub_categories.map((s) => s.name).join(", ")}> / {summarizeNames(row.sub_categories)}</span>
                            ) : row.sub_category_name && <span className="tlp-subcat"> / {row.sub_category_name}</span>}
                          </td>
                        )}
                        {showSchoolColumn && (
                          <td className="small" title={row.schools?.length > 1 ? row.schools.map((s) => s.name).join(", ") : undefined}>
                            {summarizeNames(row.schools) || row.school_name || <span className="text-muted">—</span>}
                          </td>
                        )}
                        {isSelfTickets && (
                          <td className="small" title={row.project_module_name ? `${row.project_name} / ${row.project_module_name}` : row.project_name || undefined}>
                            {row.project_name ? (
                              <div className="tlp-project-cell">
                                <span className="tlp-project-chip">{row.project_name}</span>
                                {row.project_module_name && (
                                  <span className="tlp-module-text">{row.project_module_name}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                        )}
                        {isSelfTickets && <td className="small"><AssigneeCell name={row.assigned_to_name} /></td>}
                        <td className="text-center">
                          {isSelfTickets && can("tasks", "change_status") ? (
                            <div className="tlp-status-tat">
                              <InlineStatusPicker row={row} onChanged={fetchRows} />
                              <span className={`tlp-tat tlp-tat-${getTatInfo(row).tone}`}>{getTatInfo(row).label}</span>
                            </div>
                          ) : showOwnershipTabs && activeTab === "own" && !NON_COMPLETABLE_STATUSES.has(String(row.status || "").toLowerCase()) ? (
                            // Own Tickets only, and only while the ticket is
                            // still open -- an owner-scoped status dropdown
                            // (see OwnTicketStatusPicker.jsx). A
                            // completed/closed/cancelled own ticket falls
                            // through to the plain, non-interactive
                            // StatusTatCell below (task spec: "no permitted
                            // outgoing transitions -> badge remains
                            // display-only"); its completion details stay
                            // editable via the existing Edit action instead.
                            <div className="tlp-status-tat">
                              <OwnTicketStatusPicker
                                row={row}
                                onChanged={() => fetchRows("own")}
                                onCompleteRequested={(r) => setCompleteTarget(r)}
                              />
                              <span className={`tlp-tat tlp-tat-${getTatInfo(row).tone}`}>{getTatInfo(row).label}</span>
                            </div>
                          ) : (isComplaintsTable || isSelfTickets) ? (
                            <StatusTatCell row={row} />
                          ) : (user?.role === "manager" || can("tasks", "change_status")) ? (
                            <>
                              <InlineStatusPicker row={row} onChanged={fetchRows} />
                              {isEscalated(row) && (
                                <Badge bg={null} className="tlp-pill tlp-pill-red tlp-escalated-badge" title={getTatInfo(row).label}>
                                  <FaExclamationTriangle style={{ fontSize: 9 }} /> Escalated
                                </Badge>
                              )}
                            </>
                          ) : (
                            <>
                              <Badge bg={null} className={statusPillClass(row.status)}>{titleCase(row.status)}</Badge>
                              {isEscalated(row) && (
                                <Badge bg={null} className="tlp-pill tlp-pill-red tlp-escalated-badge" title={getTatInfo(row).label}>
                                  <FaExclamationTriangle style={{ fontSize: 9 }} /> Escalated
                                </Badge>
                              )}
                            </>
                          )}
                        </td>
                        {!isComplaintsTable && (
                          <td className="text-center">
                            {row.priority ? (
                              <Badge bg={null} className={priorityPillClass(row.priority)}>{titleCase(row.priority)}</Badge>
                            ) : "—"}
                          </td>
                        )}
                        {isComplaintsTable && (
                          <td><LatestChatCell row={row} onOpen={() => setChatTaskId(row.id)} /></td>
                        )}
                        <td className="small">{formatDate(row.created_at)}</td>
                        {/* tasks.completed_at (set when the ticket is completed,
                            cleared if it is reopened) -- "—" while still open. */}
                        {isComplaintsTable && <td className="small">{formatDate(row.completed_at)}</td>}
                        {isSelfTickets && <td className="small">{formatDate(row.due_at)}</td>}
                        <td className="text-center">
                          <div className="tlp-actions">
                            <Button size="sm" variant="outline-primary" title="View" aria-label="View" onClick={() => navigate(`/${user?.role}/tasks/${row.id}`)}><FaEye /></Button>
                            <Button size="sm" variant="outline-secondary" title="Chat" aria-label="Chat" onClick={() => setChatTaskId(row.id)}><FaCommentDots /></Button>
                            {/* Own Tickets only (§16) -- an open own ticket
                                gets a maroon "Complete" action (portal
                                primary CTA, not a status color); an
                                already-completed one gets "Edit" instead, to
                                correct its completion date/resolution. */}
                            {showOwnershipTabs && activeTab === "own" && (
                              NON_COMPLETABLE_STATUSES.has(String(row.status || "").toLowerCase()) ? (
                                String(row.status || "").toLowerCase() === "completed" && (
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    title="Edit completion details"
                                    aria-label="Edit completion details"
                                    onClick={() => {
                                      // Data Analyst: the same Edit Own Ticket modal as an open
                                      // ticket (details read-only, completion editable).
                                      if (canEditOwnTicket) {
                                        setEditOwnTicketCompletion(row);
                                        setEditOwnTicketId(row.id);
                                      } else {
                                        setCompleteTarget(row);
                                      }
                                    }}
                                  >
                                    <FaPen />
                                  </Button>
                                )
                              ) : (
                                <>
                                {canEditOwnTicket && (
                                  <Button size="sm" variant="outline-secondary" title="Edit ticket" aria-label="Edit ticket" onClick={() => { setEditOwnTicketCompletion(null); setEditOwnTicketId(row.id); }}><FaPen /></Button>
                                )}
                                <Button
                                  size="sm"
                                  className="tlp-btn-primary"
                                  title="Mark ticket as completed"
                                  aria-label="Mark ticket as completed"
                                  onClick={() => setCompleteTarget(row)}
                                >
                                  <FaCheck />
                                </Button>
                                </>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={columnCount}>
                        <div className={isPolishedPage ? "tlp-empty" : "unit-empty"}>
                          <div className={isPolishedPage ? "tlp-empty-icon" : "unit-empty-icon"}><FaInbox /></div>
                          {rows.length === 0 && user?.role === "manager" ? (
                            // A valid empty result (200 []) -- never the load-error state.
                            <h5>No complaints found for your unit.</h5>
                          ) : (
                            <>
                              <h5>No matching {effectiveTitle.toLowerCase()}</h5>
                              <p>No rows match the current search/filter.</p>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {filteredRows.length > 0 && (
                <div className="tlp-pagination">
                  <span className="tlp-pagination-info">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}
                  </span>
                  <div className="tlp-pagination-controls">
                    <Button size="sm" variant="outline-secondary" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>‹</Button>
                    <span className="tlp-pagination-page">{currentPage} / {totalPages}</span>
                    <Button size="sm" variant="outline-secondary" disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}>›</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <TaskChatPanel
        taskId={chatTaskId}
        show={!!chatTaskId}
        onClose={() => setChatTaskId(null)}
        currentUserId={user?.id}
      />

      {primaryActions.some((a) => a.kind === "task-create-modal") && (
        <TaskCreateModal
          show={!!activeCreateAction}
          mode={activeCreateAction?.mode}
          onClose={closeCreateModal}
          onCreated={handleComplaintCreated}
        />
      )}

      {primaryActions.some((a) => a.kind === "own-ticket") && (
        <OwnTicketModal
          show={showOwnTicketModal}
          onClose={() => setShowOwnTicketModal(false)}
          onCreated={handleOwnTicketCreated}
        />
      )}

      {canEditOwnTicket && (
        <EditOwnTicketModal
          show={!!editOwnTicketId}
          taskId={editOwnTicketId}
          mode="full"
          completionTask={editOwnTicketCompletion}
          onClose={() => { setEditOwnTicketId(null); setEditOwnTicketCompletion(null); }}
          onSaved={() => fetchRows("own")}
        />
      )}

      {showOwnershipTabs && (
        <CompleteTicketModal
          show={!!completeTarget}
          task={completeTarget}
          onClose={() => setCompleteTarget(null)}
          onCompleted={() => fetchRows("own")}
        />
      )}

    </div>
  );
}
